// ---------- helpers ----------
function round2(v) {
  return Math.round((Number(v) || 0) * 100) / 100;
}

const safeNum = (v) => {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n : 0;
};

const toMidnight = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? new Date(d) : new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  dt.setHours(0, 0, 0, 0);
  return dt;
};

// ---------- MAIN ALLOCATION ----------
export const adjustReceiptTotalPayment = (
  instalments,
  amountPaid,
  options = {}
) => {
  const items = Array.isArray(instalments) ? instalments : [];
  const taxRate = Number(options.taxRate || 0); // GST on principal

  let remaining = round2(safeNum(amountPaid));
  const applied = [];

  const updatedInstalments = items.map((inst) => {
    // skip invalid / not completed / already fully paid
    if (!inst || !inst.isCompleted || inst.isPaid) return inst;

    const principal = round2(safeNum(inst.principal));
    const tax = round2(safeNum(inst.tax));

    // Prefer segment overdue (for this payment window) if present
    const interestBase =
      typeof inst.segmentOverdueInterest === "number"
        ? safeNum(inst.segmentOverdueInterest)
        : safeNum(inst.overdueInterest);

    const overdueTaxBase =
      typeof inst.segmentOverdueTax === "number"
        ? safeNum(inst.segmentOverdueTax)
        : safeNum(inst.overdueTax);

    let principalReceived = round2(safeNum(inst.principalReceived));
    let taxReceived = round2(safeNum(inst.taxReceived));
    let overdueInterestReceived = round2(safeNum(inst.overdueInterestReceived));
    let overdueTaxReceived = round2(safeNum(inst.overdueTaxReceived));

    // what is due *for this payment*
    let interestDue = round2(Math.max(0, interestBase));
    let overdueTaxDue = round2(Math.max(0, overdueTaxBase));
    let principalDue = round2(Math.max(0, principal - principalReceived));
    let taxDue = round2(Math.max(0, tax - taxReceived));

    // snapshot: overdue at payment (segment)
    const overdueInterestAtPayment = interestBase;
    const overdueTaxAtPayment = overdueTaxBase;
    const overdueDaysAtPayment =
      typeof inst.segmentOverdueDays === "number"
        ? inst.segmentOverdueDays
        : typeof inst.overdueDays === "number"
          ? inst.overdueDays
          : 0;

    let paidToInterest = 0;
    let paidToOverdueTax = 0;
    let paidToPrincipal = 0;
    let paidToTax = 0;

    // Quick exit if nothing due on this instalment
    if (
      interestDue <= 0 &&
      overdueTaxDue <= 0 &&
      principalDue <= 0 &&
      taxDue <= 0
    ) {
      return inst;
    }

    // 1) Overdue interest (segment) first
    if (interestDue > 0 && remaining > 0) {
      const pay = Math.min(interestDue, remaining);
      paidToInterest = round2(pay);
      overdueInterestReceived = round2(overdueInterestReceived + paidToInterest);
      remaining = round2(remaining - paidToInterest);
      interestDue = round2(Math.max(0, interestDue - paidToInterest));
    }

    // 2) Overdue tax on interest (segment)
    if (overdueTaxDue > 0 && remaining > 0) {
      const pay = Math.min(overdueTaxDue, remaining);
      paidToOverdueTax = round2(pay);
      overdueTaxReceived = round2(overdueTaxReceived + paidToOverdueTax);
      remaining = round2(remaining - paidToOverdueTax);
      overdueTaxDue = round2(Math.max(0, overdueTaxDue - paidToOverdueTax));
    }

    // 3) Base amount (principal + GST on principal) using reverse GST
    if (remaining > 0 && (principalDue > 0 || taxDue > 0)) {
      const maxBaseGross = round2(principalDue + taxDue);
      const allocBaseGross = Math.min(remaining, maxBaseGross);

      let allocPrincipal = 0;
      let allocTax = 0;

      if (taxRate > 0 && maxBaseGross > 0) {
        // reverse GST split from gross
        const taxFrac = taxRate / (100 + taxRate);
        allocTax = round2(allocBaseGross * taxFrac);
        allocPrincipal = round2(allocBaseGross - allocTax);
      } else {
        // no GST configured → everything to principal until done
        allocPrincipal = allocBaseGross;
        allocTax = 0;
      }

      // Clamp to what is actually due
      if (allocPrincipal > principalDue) {
        const extra = round2(allocPrincipal - principalDue);
        allocPrincipal = principalDue;
        allocTax = round2(allocTax + extra);
      }
      if (allocTax > taxDue) {
        const extra = round2(allocTax - taxDue);
        allocTax = taxDue;
        allocPrincipal = round2(allocPrincipal + extra);
        if (allocPrincipal > principalDue) {
          // still over principal, clamp and drop tiny rounding extra
          allocPrincipal = principalDue;
        }
      }

      // apply
      if (allocPrincipal > 0) {
        paidToPrincipal = round2(paidToPrincipal + allocPrincipal);
        principalReceived = round2(principalReceived + allocPrincipal);
        principalDue = round2(Math.max(0, principalDue - allocPrincipal));
      }

      if (allocTax > 0) {
        paidToTax = round2(paidToTax + allocTax);
        taxReceived = round2(taxReceived + allocTax);
        taxDue = round2(Math.max(0, taxDue - allocTax));
      }

      const usedGross = round2(allocPrincipal + allocTax);
      remaining = round2(Math.max(0, remaining - usedGross));
    }

    // --- tiny rounding forgiveness for last few paise/rupees ---
    const tinyDue = interestDue + overdueTaxDue + principalDue + taxDue;
    if (tinyDue > 0 && tinyDue <= 0.5) {
      // Force-clear remaining tiny balance
      overdueInterestReceived = round2(overdueInterestReceived + interestDue);
      overdueTaxReceived = round2(overdueTaxReceived + overdueTaxDue);
      principalReceived = round2(principalReceived + principalDue);
      taxReceived = round2(taxReceived + taxDue);

      interestDue = 0;
      overdueTaxDue = 0;
      principalDue = 0;
      taxDue = 0;
    }

    // instalment fully paid?
    const isPaid =
      principalDue <= 0.000001 &&
      interestDue <= 0.000001 &&
      overdueTaxDue <= 0.000001 &&
      taxDue <= 0.000001;

    const paymentWasApplied =
      paidToInterest || paidToOverdueTax || paidToPrincipal || paidToTax;

    if (paymentWasApplied) {
      applied.push({
        id: inst.id,
        name: inst.name ?? "",
        description: inst.description ?? "",
        paidToInterest,
        paidToOverdueTax,
        paidToPrincipal,
        paidToTax,
        overdueInterestAtPayment,
        overdueTaxAtPayment,
        overdueDaysAtPayment,
      });
    }

    return {
      ...inst,
      principal,
      tax,
      principalReceived,
      taxReceived,
      // keep original overdue for display; backend is source of truth
      overdueInterest: round2(safeNum(inst.overdueInterest)),
      overdueInterestReceived,
      overdueTax: round2(safeNum(inst.overdueTax)),
      overdueTaxReceived,
      isPaid: !!isPaid,
      // NOTE: backend will set paymentDate based on receipt.date
    };
  });

  const receipt = {
    amountPaid: round2(safeNum(amountPaid)),
    appliedTo: applied,
    remainingAmount: round2(remaining),
    createdAt: new Date(),
  };

  return { updatedInstalments, receipt };
};

// ---------- BACK-DATED OVERDUE PREVIEW ----------

/**
 * recalcOverdueForBackDate
 *
 * For a TRUE back-dated paymentDate, we compute
 * "segment overdue" for this payment only:
 *
 *   segmentOverdueInterest = interest on remaining principal
 *                            from max(dueDate, last payment date) -> paymentDate
 *   segmentOverdueTax      = GST on that interest (if overdueTaxRate > 0)
 *   segmentOverdueDays     = number of days in that segment
 *
 * For "today" (no back-date), we DON'T recalc – we trust backend fields.
 */
export function recalcOverdueForBackDate(
  invoices,
  paymentDate,
  settings = {}
) {
  if (!Array.isArray(invoices)) return invoices;

  const interestRate = Number(settings?.overdueInterestRate || 0);
  const overdueTaxRate = Number(settings?.overdueTaxRate || 0);

  const round2Local = (v) => Math.round((Number(v) || 0) * 100) / 100;

  const todayMid = toMidnight(new Date());
  const asOf = paymentDate ? toMidnight(paymentDate) : todayMid;

  // If no explicit back-date or same as today → skip recalc, trust backend.
  const skipRecalc =
    !paymentDate || !asOf || (todayMid && asOf.getTime() === todayMid.getTime());

  if (skipRecalc) {
    return invoices.map((inv) => ({
      ...inv,
      principal: round2Local(inv.principal || 0),
      tax: round2Local(inv.tax || 0),
      overdueInterest: round2Local(inv.overdueInterest || 0),
      overdueInterestReceived: round2Local(inv.overdueInterestReceived || 0),
      overdueTax: round2Local(inv.overdueTax || 0),
      overdueTaxReceived: round2Local(inv.overdueTaxReceived || 0),
      overdueDays:
        typeof inv.overdueDays === "number" ? inv.overdueDays : 0,
      // no segment info in this case
      segmentOverdueInterest: null,
      segmentOverdueTax: null,
      segmentOverdueDays: null,
    }));
  }

  const msPerDay = 1000 * 60 * 60 * 24;

  return invoices.map((inv) => {
    const principal = round2Local(inv.principal || 0);
    const tax = round2Local(inv.tax || 0);
    const principalReceived = round2Local(inv.principalReceived ?? 0);

    const base = {
      ...inv,
      principal,
      tax,
    };

    // fully paid principal → no new overdue
    if (inv.isPaid === true || principalReceived >= principal) {
      return {
        ...base,
        segmentOverdueInterest: 0,
        segmentOverdueTax: 0,
        segmentOverdueDays: 0,
      };
    }

    // Need a valid due date
    if (!inv.dueDate) {
      return {
        ...base,
        segmentOverdueInterest: 0,
        segmentOverdueTax: 0,
        segmentOverdueDays: 0,
      };
    }

    let due;
    if (
      inv.dueDate &&
      typeof inv.dueDate === "object" &&
      typeof inv.dueDate.seconds === "number"
    ) {
      due = new Date(inv.dueDate.seconds * 1000);
    } else {
      due = new Date(inv.dueDate);
    }
    if (Number.isNaN(due.getTime())) {
      return {
        ...base,
        segmentOverdueInterest: 0,
        segmentOverdueTax: 0,
        segmentOverdueDays: 0,
      };
    }

    const dueMid = toMidnight(due);

    // as-of date on/before due → no overdue yet
    if (!asOf || asOf <= dueMid) {
      return {
        ...base,
        segmentOverdueInterest: 0,
        segmentOverdueTax: 0,
        segmentOverdueDays: 0,
      };
    }

    // Previous payment for THIS instalment, if any
    let lastPaidMid = null;
    if (inv.paymentDate) {
      if (
        inv.paymentDate &&
        typeof inv.paymentDate === "object" &&
        typeof inv.paymentDate.seconds === "number"
      ) {
        lastPaidMid = toMidnight(new Date(inv.paymentDate.seconds * 1000));
      } else {
        lastPaidMid = toMidnight(inv.paymentDate);
      }
    }

    // Start date:
    //  - if no previous payment OR it was before/on due date => start from due date
    //  - if previous payment after due date => start from that payment date
    let startDate = dueMid;
    if (lastPaidMid && lastPaidMid > dueMid) {
      startDate = lastPaidMid;
    }

    const diffDays = Math.floor(
      (asOf.getTime() - startDate.getTime()) / msPerDay
    );

    if (diffDays <= 0) {
      return {
        ...base,
        segmentOverdueInterest: 0,
        segmentOverdueTax: 0,
        segmentOverdueDays: 0,
      };
    }

    const remainingPrincipal = Math.max(0, principal - principalReceived);
    const dailyRate =
      (Number.isNaN(interestRate) ? 0 : interestRate) / 100 / 365;

    const segInterest = round2Local(
      remainingPrincipal * dailyRate * diffDays
    );

    let segTax = 0;
    if (overdueTaxRate > 0) {
      segTax = round2Local(segInterest * (overdueTaxRate / 100));
    }

    return {
      ...base,
      // keep backend overdue fields as-is for display/outstanding
      overdueInterest: round2Local(inv.overdueInterest || 0),
      overdueInterestReceived: round2Local(inv.overdueInterestReceived || 0),
      overdueTax: round2Local(inv.overdueTax || 0),
      overdueTaxReceived: round2Local(inv.overdueTaxReceived || 0),
      overdueDays:
        typeof inv.overdueDays === "number" ? inv.overdueDays : 0,

      // segment used ONLY for this receipt allocation
      segmentOverdueInterest: segInterest,
      segmentOverdueTax: segTax,
      segmentOverdueDays: diffDays,
    };
  });
}